import { supabase } from "@/lib/supabase";

/**
 * This class exposes the `requestAccessToken` method that can be used to initiate the authorization flow with Google.
 *
 * A new access token is requested from Google by triggering a UX flow from the "Google 3P Authorization" library
 * with the 'code model'. See the following references for more information:
 *
 * - https://developers.google.com/identity/oauth2/web/reference/js-reference
 * - https://developers.google.com/identity/oauth2/web/guides/use-code-model
 *
 * Once the user has authorized the application, a code is returned to the callback function, which is then sent to the
 * Supabase Edge Function `api/auth/code-exchange` to exchange it for an access token and refresh token with the requested
 * scopes.
 *
 * The access token is stored on the server and never returned to the client. As such, all calls to Google APIs are
 * performed on the server using Supabase Edge Functions.
 *
 * NOTE: The token retrieved by this class is an 'authorization' token, which is different than the 'authentication' JWT
 * returned by the Google sign in button and one-tap sign in flow. The authentication JWT retrieved by the Google sign
 * in button is used to authenticate and log in with Supabase, but cannot be used to call any Google APIs due to the lack
 * of any scopes attached to it. The Google authorization token requested here can be requested on-demand after sign in,
 * and can be used to call Google APIs by the back-end according to the scopes specified in the constructor.
 */
class GoogleAuthorizationService {
  private codeClient: google.accounts.oauth2.CodeClient | null = null;

  private accessTokenPromise: Promise<void> | null = null;
  private resolve: (() => void) | null = null;
  private reject: ((reason: Error) => void) | null = null;

  constructor(private readonly requiredScopes: Set<string>) {}

  /**
   * Initiates the process authorize the current user. No tokens are returned directly from this method; A successful
   * return of this method indicates that the user has authorized the application and the access/refresh tokens are now
   * stored on the server.
   */
  public async authorizeCurrentUser(): Promise<void> {
    // Wait for the result of an existing token request to complete, if any, to prevent concurrent requests
    if (this.accessTokenPromise) await this.accessTokenPromise;

    // Create the code client if it does not exist yet
    if (!this.codeClient) {
      // Get the current user's email address from the Supabase session that was created during authentication
      // This will allow the user to skip the user selection screen for this authorization step
      const session = await supabase.auth.getSession();
      const login_hint = session.data.session?.user.email;

      this.codeClient = google.accounts.oauth2.initCodeClient({
        client_id: process.env.GOOGLE_OAUTH_CLIENT_ID,
        scope: [...this.requiredScopes].join(" "),
        ux_mode: "popup",
        login_hint,
        callback: this.tokenCallback.bind(this), // eslint-disable-line @typescript-eslint/no-misused-promises
        error_callback: this.tokenCallback.bind(this), // eslint-disable-line @typescript-eslint/no-misused-promises
      });
    }

    // Create a new promise that will be resolved when the token request completes
    this.accessTokenPromise = new Promise<void>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });

    try {
      // Start a new token request and wait for the result
      this.codeClient.requestCode();
      await this.accessTokenPromise;
    } finally {
      // Reset state before exiting returning the result
      this.accessTokenPromise = null;
      this.resolve = null;
      this.reject = null;
    }
  }

  private async tokenCallback(
    tokenResponse: google.accounts.oauth2.CodeResponse | google.accounts.oauth2.ClientConfigError,
  ): Promise<void> {
    // Throw an error if our state is somehow corrupted and we have no promise to resolve
    // This should never happen because the promise is set when the token request flow is started
    if (!this.resolve || !this.reject)
      throw new Error("Access token callback was called but there is no promise awaiting it");

    try {
      // Attempt to parse the token response and resolve the promise
      await this.processTokenResponse(tokenResponse);
      this.resolve();
    } catch (cause) {
      // On error, reject the promise
      const error = new Error("An error occurred during Google authorization: " + (cause as Error).message, { cause });
      this.reject(error);
    }
  }

  private async processTokenResponse(
    codeResponse: google.accounts.oauth2.CodeResponse | google.accounts.oauth2.ClientConfigError,
  ) {
    // Fail if a client-side error occurred during the token request (i.e. error_callback was invoked)
    if (codeResponse instanceof Error) throw codeResponse;

    // Fail if the token request failed
    if (codeResponse.error) {
      console.error("Google authorization failed: ", codeResponse);
      throw new Error(codeResponse.error_description);
    }

    // Fail if the user did not authorize all the requested scopes
    const grantedScopes = new Set(codeResponse.scope.split(" "));
    const missingScopes = [...this.requiredScopes].filter((scope) => !grantedScopes.has(scope)).join(", ");
    if (missingScopes.length)
      throw new Error("User did not authorize all requested permissions. Missing scopes: " + missingScopes);

    // Send the code to the server to exchange it for an access token
    // NOTE: For some reason we can't send this request using `supabase.functions.invoke` because it refuses to send a request body
    const tokenResponse = await fetch(process.env.SUPABASE_URL + "/functions/v1/api/auth/code-exchange", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${(await supabase.auth.getSession()).data.session!.access_token}`,
        "Content-Type": "application/json",

        // Add a custom header to trigger CORS and protect against CSRF (see: https://stackoverflow.com/a/22533680/1988326)
        "X-Requested-With": "XmlHttpRequest",
      },
      body: JSON.stringify({ code: codeResponse.code }),
    });

    // Throw an error if the function invocation failed
    if (!tokenResponse.ok) {
      const responseText = await tokenResponse.text();
      throw new Error(
        `Google token exchange failed with code ${tokenResponse.status.toFixed()} (${tokenResponse.statusText}): ${responseText}`,
      );
    }
  }
}

// Export a singleton instance of the GoogleAuthorizationTokenFetcher class with all the scopes required for this application
export default new GoogleAuthorizationService(new Set(["https://www.googleapis.com/auth/drive.readonly"]));
